import { AxiosResponse } from 'axios';
import { ProductDescriptionSearchResponse, ProductDescriptionSearchCriteria, ProductDescriptionAnalysisResponse, ArticleFailureSearchCriteria, ArticleFailureListResponse } from './types';
import { createApiClient } from 'services/apiClient';
import { performBulkDelete, BulkDeleteResult, BulkDeleteOptions } from 'services/bulkDelete';
import { API_BASE_URL } from 'config/api';

const apiClient = createApiClient('PRODUCT_DESCRIPTION');

/**
 * Remap API field names to frontend field names.
 * The API returns `article_number` / `scope_of_delivery` but the frontend
 * types use `articlenumber` / `lieferumfang`.
 */
function remapParsedDocument(doc: any): any {
  if (!doc) return doc;
  const remapped = { ...doc };
  remapped.articlenumber = remapped.articlenumber ?? remapped.article_number ?? null;
  remapped.lieferumfang = remapped.lieferumfang ?? remapped.scope_of_delivery ?? [];
  delete remapped.article_number;
  delete remapped.scope_of_delivery;
  return remapped;
}

function normalizeSourceUrls(state: any): string[] {
  const candidateUrls = state?.document_source_urls ?? state?.source_urls;
  if (!Array.isArray(candidateUrls)) {
    return [];
  }
  return candidateUrls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0);
}

export default class ProductDescriptionService {
  static async searchProductDescriptions(tenantId: string, criteria: ProductDescriptionSearchCriteria): Promise<ProductDescriptionSearchResponse> {
    try {
      console.log('📦 [PRODUCT_DESCRIPTION] Searching product descriptions:', {
        tenantId,
        criteria,
        url: `${API_BASE_URL}/business-documents/product-descriptions/search/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      
      // Add query parameters
      if (criteria.document_status) params.append('document_status', criteria.document_status);
      if (criteria.created_at_start) params.append('created_at_start', criteria.created_at_start);
      if (criteria.created_at_end) params.append('created_at_end', criteria.created_at_end);
      if (criteria.document_state_id) params.append('document_state_id', criteria.document_state_id);
      if (criteria.has_been_sent != null) params.append('has_been_sent', criteria.has_been_sent.toString());
      if (criteria.parent_process_state_id) params.append('parent_process_state_id', criteria.parent_process_state_id);
      if (criteria.articlenumber) params.append('articlenumber', criteria.articlenumber);
      if (criteria.sku) params.append('sku', criteria.sku);
      if (criteria.ean) params.append('ean', criteria.ean);
      if (criteria.max_retry_reached != null) params.append('max_retry_reached', criteria.max_retry_reached.toString());
      if (criteria.has_errors != null) params.append('has_errors', criteria.has_errors.toString());
      if (criteria.has_work_description != null) params.append('has_work_description', criteria.has_work_description.toString());
      if (criteria.days_back) params.append('days_back', criteria.days_back.toString());
      if (criteria.limit) params.append('limit', criteria.limit.toString());
      
      // Always include parsed document and processing result
      params.append('include_parsed_document', 'true');
      params.append('include_processing_result', 'true');
      
      const url = `/business-documents/product-descriptions/search/${tenantId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<ProductDescriptionSearchResponse> = await apiClient.get(url);

      // Normalize API field names in search results to frontend type shape.
      // This keeps list/table views compatible when backend returns snake_case keys.
      const mappedData: ProductDescriptionSearchResponse = {
        ...response.data,
        results: (response.data.results || []).map((item: any) => ({
          ...item,
          document_source_urls: normalizeSourceUrls(item),
          parsed_document: remapParsedDocument(item.parsed_document),
          edited_parsed_document: remapParsedDocument(item.edited_parsed_document),
          processing_result: item.processing_result
            ? {
                ...item.processing_result,
                enriched_document: remapParsedDocument(item.processing_result.enriched_document),
              }
            : item.processing_result,
        })),
      };
      
      console.log('✅ [PRODUCT_DESCRIPTION] Product description search response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: mappedData.tenant_id,
          total_results: mappedData.total_results,
          result_count: mappedData.results?.length || 0
        }
      });
      
      return mappedData;
    } catch (error: any) {
      console.error('❌ [PRODUCT_DESCRIPTION] Error searching product descriptions:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to search product descriptions');
    }
  }

  static async getProductDescriptionStateDetail(tenantId: string, documentStateId: string): Promise<any> {
    try {
      console.log('📦 [PRODUCT_DESCRIPTION] Fetching product description state detail:', {
        tenantId,
        documentStateId,
        url: `${API_BASE_URL}/business-documents/product-descriptions/state/${tenantId}/${documentStateId}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/business-documents/product-descriptions/state/${tenantId}/${documentStateId}`);
      
      // Remap parsed documents so API field names match frontend types
      const processingResult = response.data.processing_result
        ? {
            ...response.data.processing_result,
            enriched_document: remapParsedDocument(response.data.processing_result.enriched_document),
          }
        : response.data.processing_result;

      // Map the backend response to match the frontend's expected structure
      const mappedData = {
        document_state_id: response.data.document_state_id,
        tenant_id: tenantId,
        document_status: response.data.document_status,
        document_source_type: response.data.document_source_type,
        document_source_urls: normalizeSourceUrls(response.data),
        has_been_sent: response.data.has_been_sent,
        is_processing: response.data.is_processing ?? false,
        has_work_description: response.data.parsed_document?.work_descriptions?.length > 0 || false,
        work_description_entries: response.data.work_description_entries || [],
        backend_id: response.data.backend_id,
        retry_count: response.data.retry_count,
        max_retry_reached: response.data.max_retry_reached,
        last_error_message: response.data.last_error_message,
        error_history: response.data.error_history || [],
        parsed_document: remapParsedDocument(response.data.parsed_document),
        edited_parsed_document: remapParsedDocument(response.data.edited_parsed_document),
        parent_process_state_id: response.data.parent_process_state_id,
        parent_process_type: response.data.parent_process_type || null,
        parent_process_container: response.data.parent_process_container || null,
        product_description_email_eml_path: response.data.product_description_email_eml_path,
        email_eml_path: response.data.product_description_email_eml_path || response.data.email_eml_path, // For backward compatibility
        doc_path: response.data.doc_path,
        processing_result: processingResult,
        created_at_date: response.data.created_at_date,
        hitl_required: response.data.hitl_required,
        hitl_reason: response.data.hitl_reason,
        hitl_rule_ids: response.data.hitl_rule_ids,
        hitl_evaluation_history: response.data.hitl_evaluation_history,
      };
      
      console.log('✅ [PRODUCT_DESCRIPTION] Product description state detail response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: tenantId,
          document_state_id: mappedData.document_state_id,
          document_status: mappedData.document_status
        }
      });
      
      return mappedData;
    } catch (error: any) {
      console.error('❌ [PRODUCT_DESCRIPTION] Error fetching product description state detail:', {
        tenantId,
        documentStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch product description state detail');
    }
  }

  static async updateProductDescription(tenantId: string, documentStateId: string, productDescriptionData: any): Promise<any> {
    try {
      console.log('📝 [PRODUCT_DESCRIPTION] Updating product description:', {
        tenantId,
        documentStateId,
        url: `${API_BASE_URL}/business-documents/product-descriptions/state/${tenantId}/${documentStateId}`,
        method: 'PUT'
      });
      
      const url = `/business-documents/product-descriptions/state/${tenantId}/${documentStateId}`;
      
      const response: AxiosResponse<any> = await apiClient.put(url, productDescriptionData);
      
      console.log('✅ [PRODUCT_DESCRIPTION] Update product description response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [PRODUCT_DESCRIPTION] Error updating product description:', {
        tenantId,
        documentStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to update product description');
    }
  }

  static async getProductDescriptionAnalysis(tenantId: string, days: number = 30): Promise<ProductDescriptionAnalysisResponse> {
    try {
      console.log('📊 [PRODUCT_DESCRIPTION] Getting product description analysis:', {
        tenantId,
        days,
        url: `${API_BASE_URL}/business-documents/product-descriptions/analysis/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      params.append('days', days.toString());
      
      const url = `/business-documents/product-descriptions/analysis/${tenantId}?${params.toString()}`;
      
      const response: AxiosResponse<ProductDescriptionAnalysisResponse> = await apiClient.get(url);
      
      console.log('✅ [PRODUCT_DESCRIPTION] Product description analysis response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_descriptions: response.data.total_descriptions,
          period_days: response.data.period_days,
          descriptions_with_errors: response.data.descriptions_with_errors
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [PRODUCT_DESCRIPTION] Error getting product description analysis:', {
        tenantId,
        days,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to get product description analysis');
    }
  }

  static async deleteProductDescriptionState(
    tenantId: string,
    documentStateId: string,
    force: boolean = false
  ): Promise<void> {
    try {
      console.log('🗑️ [PRODUCT_DESCRIPTION] Deleting product description state:', {
        tenantId,
        documentStateId,
        force,
        url: `${API_BASE_URL}/business-documents/product-descriptions/state/${tenantId}/${documentStateId}`,
        method: 'DELETE'
      });
      
      const params = new URLSearchParams();
      if (force) {
        params.append('force', 'true');
      }
      
      const url = `/business-documents/product-descriptions/state/${tenantId}/${documentStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.delete(url);
      
      console.log('✅ [PRODUCT_DESCRIPTION] Delete product description state response:', {
        status: response.status,
        statusText: response.statusText
      });
    } catch (error: any) {
      console.error('❌ [PRODUCT_DESCRIPTION] Error deleting product description state:', {
        tenantId,
        documentStateId,
        force,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      
      // Re-throw with more detailed error information
      if (error.response?.data?.detail) {
        const errorMessage = new Error(error.response.data.detail);
        (errorMessage as any).status = error.response?.status;
        throw errorMessage;
      }
      throw error;
    }
  }

  static async getProductDescriptionFailures(
    tenantId: string,
    criteria: ArticleFailureSearchCriteria = {}
  ): Promise<ArticleFailureListResponse> {
    try {
      console.log('🔴 [PRODUCT_DESCRIPTION_FAILURES] Fetching product description failures:', {
        tenantId,
        criteria,
        url: `${API_BASE_URL}/business-documents/product-descriptions/failures/${tenantId}`,
        method: 'GET'
      });

      const params = new URLSearchParams();
      if (criteria.parent_email_processing_state_id) params.append('parent_email_processing_state_id', criteria.parent_email_processing_state_id);
      if (criteria.failure_stage) params.append('failure_stage', criteria.failure_stage);
      if (criteria.article_number) params.append('article_number', criteria.article_number);
      if (criteria.ean) params.append('ean', criteria.ean);
      if (criteria.vendor_key) params.append('vendor_key', criteria.vendor_key);
      if (criteria.created_at_start) params.append('created_at_start', criteria.created_at_start);
      if (criteria.created_at_end) params.append('created_at_end', criteria.created_at_end);
      if (criteria.days_back != null) params.append('days_back', criteria.days_back.toString());
      if (criteria.limit != null) params.append('limit', criteria.limit.toString());
      if (criteria.offset != null) params.append('offset', criteria.offset.toString());

      const url = `/business-documents/product-descriptions/failures/${tenantId}${params.toString() ? `?${params.toString()}` : ''}`;

      const response: AxiosResponse<ArticleFailureListResponse> = await apiClient.get(url);

      console.log('✅ [PRODUCT_DESCRIPTION_FAILURES] Failures response:', {
        count: response.data.count,
        status: response.status
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ [PRODUCT_DESCRIPTION_FAILURES] Error fetching failures:', {
        tenantId,
        criteria,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch product description failures');
    }
  }

  static async bulkDeleteProductDescriptionStates(
    tenantId: string,
    ids: string[],
    options?: BulkDeleteOptions,
  ): Promise<BulkDeleteResult> {
    return performBulkDelete(
      apiClient,
      'PRODUCT_DESCRIPTION',
      `/business-documents/product-descriptions/state/${tenantId}/bulk-delete`,
      ids,
      options,
    );
  }
}
